import React, { useState, useEffect } from 'react';
import { Spin, Pagination, Modal, Button, Table, Tag, Input } from 'antd';
import { useLazyGetWidgetLogsQuery, useLazyGetWidgetLogDetailQuery, useLazyGetWidgetLogsByDashboardQuery } from '../../services/dashboardSlice';
import { toast } from '../../components/Toast/toast';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { ColumnsType } from 'antd/es/table';

interface LogItem {
  _id: string;
  timestamp?: string;
  created_at?: string;
  question?: string;
  status?: string;
  dashboard_id?: string;
  [key: string]: any;
}

const WidgetLogs: React.FC = () => {
  // Get current workspace ID reactively from WorkspaceContext
  const { currentWorkspace, getCurrentWorkspaceName } = useWorkspace();
  const workspaceId = currentWorkspace || '';
  const workspaceName = getCurrentWorkspaceName();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedLog, setSelectedLog] = useState<LogItem | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [dashboardId, setDashboardId] = useState('');

  const [getWidgetLogs, { data, isLoading, isError, error }] = useLazyGetWidgetLogsQuery();
  const [getWidgetLogsByDashboard, { data: dashboardData, isLoading: isLoadingDashboard, isError: isDashboardError, error: dashboardError }] = useLazyGetWidgetLogsByDashboardQuery();
  const [getLogDetail, { data: logDetailData, isLoading: isLoadingDetail }] = useLazyGetWidgetLogDetailQuery();

  // Fetch logs function
  const fetchLogs = (newPage = 1, newPageSize = pageSize) => {
    if (!workspaceId) return;
    setPage(newPage);
    if (dashboardId.trim()) {
      getWidgetLogsByDashboard({ dashboard_id: dashboardId.trim(), workspace_id: workspaceId, page: newPage, page_size: newPageSize });
    } else {
      getWidgetLogs({ workspace_id: workspaceId, page: newPage, page_size: newPageSize });
    }
  };

  // Auto-fetch on mount and when workspace changes
  useEffect(() => {
    if (workspaceId) {
      fetchLogs();
    }
  }, [workspaceId, pageSize]);

  // Combined data from either API
  const currentData = dashboardId.trim() ? dashboardData : data;
  const currentLoading = dashboardId.trim() ? isLoadingDashboard : isLoading;
  const currentError = dashboardId.trim() ? isDashboardError : isError;
  const currentErrorData = dashboardId.trim() ? dashboardError : error;

  useEffect(() => {
    if (currentData) {
      setTotalRecords(currentData.total || currentData.logs?.length || 0);
    }
  }, [currentData]);

  useEffect(() => {
    if (currentError) {
      const errorMessage = (currentErrorData as any)?.data?.message || 'Failed to fetch widget logs';
      toast.error(errorMessage);
    }
  }, [currentError, currentErrorData]);

  const handleDashboardSearch = () => {
    fetchLogs();
  };

  const handleClearDashboard = () => {
    setDashboardId('');
    if (workspaceId) {
      setPage(1);
      // Fetch all logs after clearing dashboard filter
      getWidgetLogs({ workspace_id: workspaceId, page: 1, page_size: pageSize });
    }
  };

  // Update selectedLog when detail data is fetched
  useEffect(() => {
    if (logDetailData) {
      setSelectedLog(logDetailData);
    }
  }, [logDetailData]);

  const handlePageChange = (newPage: number, newPageSize?: number) => {
    const size = newPageSize || pageSize;
    if (newPageSize) setPageSize(newPageSize);
    fetchLogs(newPage, size);
  };

  const handleViewDetail = (log: LogItem) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
    // Fetch full detail
    if (log._id) {
      getLogDetail({ id: log._id });
    }
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
  };

  const logsData: LogItem[] = currentData?.logs || currentData?.data || (Array.isArray(currentData) ? currentData : []);

  // Table columns - show only essential info
  const columns: ColumnsType<LogItem> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (text: string, record: LogItem) => {
        const dateStr = text || record.created_at;
        if (!dateStr) return <span className="text-gray-400">-</span>;
        try {
          return (
            <span className="text-gray-700 font-medium">
              {new Date(dateStr).toLocaleString()}
            </span>
          );
        } catch {
          return dateStr;
        }
      },
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true,
      render: (text: string) => (
        <span className="text-gray-800" title={text}>
          {text || <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        if (!status) return <span className="text-gray-400">-</span>;
        const colorMap: Record<string, string> = {
          success: 'green',
          completed: 'green',
          error: 'red',
          failed: 'red',
          pending: 'orange',
          processing: 'blue',
        };
        const color = colorMap[status.toLowerCase()] || 'default';
        return (
          <Tag
            color={color}
            style={{
              borderRadius: '12px',
              padding: '2px 10px',
              fontWeight: 500,
              textTransform: 'capitalize'
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Dashboard',
      dataIndex: 'dashboard_id',
      key: 'dashboard_id',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <span className="text-gray-600 text-sm">
          {text || <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_: any, record: LogItem) => (
        <span
          onClick={() => handleViewDetail(record)}
          className="text-indigo-600 hover:text-indigo-800 cursor-pointer font-medium text-sm hover:underline"
        >
          View
        </span>
      ),
    },
  ];

  // Render detail content
  const renderDetailContent = () => {
    if (!selectedLog) return null;

    return (
      <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
        {Object.entries(selectedLog).map(([key, value]) => {
          // Skip internal fields
          if (key === '_id' || key === '__v') return null;

          // Format key for display
          const formattedKey = key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());

          return (
            <div
              key={key}
              className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors"
            >
              <div className="text-sm font-semibold text-indigo-600 uppercase tracking-wide mb-2">
                {formattedKey}
              </div>
              <div className="text-base text-gray-800">
                {typeof value === 'object' && value !== null ? (
                  <pre
                    className="bg-white p-3 rounded-md text-sm overflow-x-auto border border-gray-100"
                    style={{
                      fontFamily: 'Monaco, Consolas, monospace',
                      lineHeight: 1.5,
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : key.toLowerCase().includes('timestamp') || key.toLowerCase().includes('created') ? (
                  <span className="font-medium">
                    {value ? new Date(String(value)).toLocaleString() : '-'}
                  </span>
                ) : (
                  <span className="break-words">{String(value ?? '-')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Widget Logs</h1>
          <p className="text-sm text-gray-500">
            Workspace: <span className="font-semibold text-indigo-600">{workspaceName || workspaceId || 'Not set'}</span>
          </p>
        </div>

        {/* Dashboard Filter */}
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Dashboard ID
          </label>
          <div className="flex gap-3">
            <Input
              placeholder="Enter dashboard ID..."
              value={dashboardId}
              onChange={(e) => setDashboardId(e.target.value)}
              onPressEnter={handleDashboardSearch}
              className="max-w-md"
              style={{ borderRadius: '6px' }}
            />
            <span
              onClick={workspaceId ? handleDashboardSearch : undefined}
              className={`px-4 py-1.5 rounded-md text-sm font-medium cursor-pointer ${
                workspaceId
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Search
            </span>
            {dashboardId && (
              <Button
                onClick={handleClearDashboard}
                style={{ borderRadius: '6px' }}
              >
                Clear
              </Button>
            )}
          </div>
          {dashboardId.trim() && (
            <p className="mt-2 text-xs text-gray-500">
              Showing logs for dashboard: <span className="font-medium text-gray-700">{dashboardId}</span>
            </p>
          )}
        </div>

        {/* Results Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {currentLoading ? (
            <div className="flex justify-center items-center py-24">
              <Spin size="large" />
            </div>
          ) : !workspaceId ? (
            <div className="text-center py-24 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No workspace selected</p>
              <p className="text-sm mt-1">Please select a workspace to view logs</p>
            </div>
          ) : logsData.length > 0 ? (
            <>
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                <span className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-800">{logsData.length}</span> records
                  {totalRecords > 0 && (
                    <> of <span className="font-semibold text-gray-800">{totalRecords}</span> total</>
                  )}
                </span>
              </div>
              <Table
                columns={columns}
                dataSource={logsData}
                rowKey={(record) => record._id || Math.random().toString()}
                pagination={false}
                size="middle"
                className="widget-logs-table"
                rowClassName="hover:bg-indigo-50/30 transition-colors"
                style={{ borderRadius: 0 }}
              />
              {totalRecords > pageSize && (
                <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end">
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={totalRecords}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={['10', '25', '50', '100']}
                    showTotal={(total, range) => (
                      <span className="text-sm text-gray-500">
                        {range[0]}-{range[1]} of {total} items
                      </span>
                    )}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-lg font-medium">No logs found</p>
              <p className="text-sm mt-1">There are no logs for this workspace yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 pb-2">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 m-0">Log Details</h3>
              <p className="text-xs text-gray-500 m-0">Full information for this log entry</p>
            </div>
          </div>
        }
        open={detailModalOpen}
        onCancel={handleCloseModal}
        footer={
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleCloseModal}
              style={{
                borderRadius: '8px',
                padding: '0 20px',
                height: '36px'
              }}
            >
              Close
            </Button>
          </div>
        }
        width={720}
        centered
        styles={{
          header: { borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' },
          body: { padding: '20px' },
          footer: { borderTop: '1px solid #f0f0f0', paddingTop: '12px' }
        }}
      >
        {isLoadingDetail ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Spin size="large" />
            <p className="mt-4 text-sm text-gray-500">Loading details...</p>
          </div>
        ) : (
          renderDetailContent()
        )}
      </Modal>

      {/* Custom styles for table */}
      <style>{`
        .widget-logs-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          font-weight: 600;
          color: #475569;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 14px 16px;
          border-bottom: 1px solid #e2e8f0;
        }
        .widget-logs-table .ant-table-tbody > tr > td {
          padding: 14px 16px;
          border-bottom: 1px solid #f1f5f9;
        }
        .widget-logs-table .ant-table-tbody > tr:last-child > td {
          border-bottom: none;
        }
        .widget-logs-table .ant-table-row:hover > td {
          background: rgba(99, 102, 241, 0.04) !important;
        }
      `}</style>
    </div>
  );
};

export default WidgetLogs;
